/**
 * /debug/composition-language ✦ iter202
 * ─────────────────────────────────────────────────────────────
 * Internal diagnostic surface. Renders one camera at a time
 * through every profile in the composition language so we can
 * verify visual parity across surfaces WITHOUT having to walk
 * through Booth OS / Lens Notes / Pocket Booth manually.
 *
 * Per Craig (iter200 close-out): "this route will become core
 * infrastructure tooling, not optional debugging." Built ALONGSIDE
 * the Lens Notes migration (iter202) so the first stress-test
 * surface ships with the regression-testing tool.
 *
 * What it surfaces:
 *
 *   • Live preview through the selected profile.
 *   • Profile configuration (aspect ratio, resolution, mirror
 *     mode, safe zone, headroom guide, subject anchor, capture
 *     distance, frame rate, audio).
 *   • Runtime telemetry:
 *       — videoWidth × videoHeight (the live signature)
 *       — dimensionsStable (the iter200 stabilisation flag)
 *       — mirrorPreview / mirrorExport (the resolved booleans)
 *       — devicePixelRatio
 *       — hitRect (composite display rect, when present)
 *       — assertCanvasInvariants() report for the lens profile
 *   • FramingGuide rendered with visibleOverride=true so the
 *     corner-tick + headroom-hairline geometry can be inspected
 *     against the real video frame.
 *
 * Hidden from production navigation. Direct URL access only.
 */
import React, { useRef, useState } from 'react';
import { useBoothCameraPipeline } from '../../hooks/useBoothCameraPipeline';
import { useParticipationAudioPipeline } from '../../hooks/useParticipationAudioPipeline';
import {
  BOOTH_CAMERA_PROFILES, MIRROR,
} from '../../lib/booth-os/boothCameraProfiles';
import { PARTICIPATION_AUDIO_PROFILES } from '../../lib/booth-os/participationAudioProfiles';
import { assertCanvasInvariants } from '../../lib/booth-os/lensCoordinateSpaces';
import {
  PIPELINE_VERSION,
  deviceFingerprint,
  saveGolden,
  loadGolden,
  downloadGolden,
  diffAudio,
  profileHash as hashProfile,
} from '../../lib/booth-os/goldenReferences';

const PROFILE_NAMES = Object.keys(BOOTH_CAMERA_PROFILES);
const AUDIO_PROFILE_NAMES = Object.keys(PARTICIPATION_AUDIO_PROFILES);

/**
 * AudioPanel ✦ iter205 — voice-side diagnostic.
 *
 * Live telemetry for the participation audio pipeline:
 *   • peakLevel        — current RMS (0..1)
 *   • presenceFloor    — slow EMA of quiet-window RMS (the "room
 *                         when nobody is speaking")
 *   • clipping         — flagged when peak crosses the profile's
 *                         clippingGuardLinear
 *   • status / mic devices / mime negotiation
 *
 * Visual meter is a single vertical hairline bar — restrained,
 * matches the platform's "authored atmosphere" idiom rather than
 * a podcast-style VU meter.
 */
const AudioPanel = ({ profileName }) => {
  const mic = useParticipationAudioPipeline({ profile: profileName });

  React.useEffect(() => {
    mic.start();
    return () => mic.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileName]);

  const pct = Math.min(100, Math.round(mic.peakLevel * 220));
  const floorPct = Math.min(100, Math.round(mic.presenceFloor * 220));

  return (
    <div data-testid={`audio-panel-${profileName}`} style={{
      display: 'grid', gridTemplateColumns: 'minmax(80px, 100px) 1fr',
      gap: 28, padding: 18, marginTop: 18,
      border: '1px solid rgba(212,175,94,0.18)',
      borderRadius: 14, background: 'rgba(0,0,0,0.4)',
    }}>
      {/* Vertical hairline meter — gold accent, ivory floor mark. */}
      <div style={{
        position: 'relative', height: 240,
        background: 'linear-gradient(180deg, rgba(212,175,94,0.04), rgba(212,175,94,0.01))',
        borderRadius: 6, overflow: 'hidden',
      }} data-testid={`audio-meter-${profileName}`}>
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          height: `${pct}%`,
          background: mic.clipping
            ? 'linear-gradient(0deg, #e0392a, rgba(224,57,42,0.4))'
            : 'linear-gradient(0deg, rgba(212,175,94,0.85), rgba(212,175,94,0.3))',
          transition: 'height 80ms linear',
        }} />
        <div style={{
          position: 'absolute', left: 0, right: 0,
          bottom: `${floorPct}%`,
          height: 1, background: 'rgba(248,232,200,0.7)',
        }} data-testid={`audio-presence-floor-${profileName}`} />
      </div>
      <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
        <h3 style={{
          margin: 0, padding: '0 0 8px',
          color: '#c19a4b', fontFamily: 'Georgia, serif',
          fontStyle: 'italic', fontWeight: 500, fontSize: 18,
        }}>
          {profileName} ✦ (audio)
        </h3>
        <Row k="status" v={mic.status} />
        <Row k="peakLevel" v={mic.peakLevel.toFixed(3)} />
        <Row k="presenceFloor" v={mic.presenceFloor.toFixed(4)} />
        <Row k="clipping" v={mic.clipping ? 'YES' : 'no'} />
        <Row k="sampleRate" v={mic.profile.sampleRate} />
        <Row k="lowCutHz" v={mic.profile.lowCutHz} />
        <Row k="compressorRatio" v={mic.profile.compressorRatio} />
        <Row k="silenceTrimRMS" v={mic.profile.silenceTrimRMS} />
        <Row k="tailSilenceMaxMs" v={mic.profile.tailSilenceMaxMs} />
        <Row k="maxDurationSec" v={mic.profile.maxDurationSec} />
        <Row k="recordingMime" v={mic.recordingMime || '—'} />
        <Row k="micDevices" v={mic.devices.length} />
        {mic.error && (
          <div style={{ marginTop: 12, color: '#e0392a' }} data-testid={`audio-error-${profileName}`}>
            {mic.error}
          </div>
        )}
        <GoldenAudioControls mic={mic} profileName={profileName} />
      </div>
    </div>
  );
};

/**
 * GoldenAudioControls ✦ iter207 — capture / compare / export a
 * golden audio reference for the current profile + device.
 *
 * Bundle payload preserves:
 *   • rawRoomToneRMS              (baseline "room when quiet")
 *   • presenceFloorSample         (live presence floor at capture)
 *   • peakLevelTrace              (~30 samples — recent RMS history)
 *   • voicePresenceMoments        (speaking-window map)
 *   • micDeviceLabel              (which mic the truth is anchored to)
 *   • recordingMime               (negotiated codec)
 */
const GoldenAudioControls = ({ mic, profileName }) => {
  const [golden, setGolden] = useState(() => loadGolden('audio', profileName));
  const [peakTrace, setPeakTrace] = React.useState([]);

  // Maintain a small rolling peak trace so the captured golden
  // carries ~6 seconds of recent loudness rhythm, not just the
  // instant-capture frame.
  React.useEffect(() => {
    if (mic.status !== 'live' && mic.status !== 'recording') return undefined;
    const id = setInterval(() => {
      setPeakTrace((arr) => {
        const next = [...arr, mic.peakLevel];
        if (next.length > 30) next.shift();
        return next;
      });
    }, 200);
    return () => clearInterval(id);
  }, [mic.status, mic.peakLevel]);

  const save = () => {
    const defaultMic = mic.devices.find((d) => d.deviceId === 'default') || mic.devices[0];
    const { bundle } = saveGolden({
      kind: 'audio',
      profileName,
      profileConfigSnapshot: mic.profile,
      payload: {
        rawRoomToneRMS: mic.rawRoomToneRMS || 0,
        presenceFloorSample: mic.presenceFloor || 0,
        peakLevelTrace: peakTrace.slice(),
        voicePresenceMoments: mic.voicePresenceMoments.slice(),
        micDeviceLabel: defaultMic?.label || 'unknown',
        recordingMime: mic.recordingMime || '',
      },
    });
    setGolden(bundle);
  };

  const wipe = () => {
    saveGolden({ kind: 'audio', profileName, profileConfigSnapshot: mic.profile, payload: null });
    setGolden(null);
  };

  const drift = golden?.payload ? diffAudio({
    pipelineVersion: PIPELINE_VERSION,
    profileHash: hashProfile(mic.profile),
    rawRoomToneRMS: mic.rawRoomToneRMS || 0,
    presenceFloorSample: mic.presenceFloor || 0,
    peakLevelTrace: peakTrace,
    voicePresenceMoments: mic.voicePresenceMoments,
    micDeviceLabel: (mic.devices.find((d) => d.deviceId === 'default') || mic.devices[0])?.label || 'unknown',
    recordingMime: mic.recordingMime || '',
  }, golden) : null;

  return (
    <div style={{ marginTop: 14 }} data-testid={`golden-audio-${profileName}`}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={save}
          disabled={mic.status === 'idle' || mic.status === 'denied' || mic.status === 'error'}
          data-testid={`golden-audio-save-${profileName}`}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: '1px solid rgba(212,175,94,0.35)',
            background: 'rgba(212,175,94,0.10)',
            color: '#f8e8c8',
            cursor: 'pointer',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: 11,
          }}
        >Capture golden ✦</button>
        <button
          type="button"
          onClick={() => golden && downloadGolden(golden)}
          disabled={!golden}
          data-testid={`golden-audio-export-${profileName}`}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: '1px solid rgba(212,175,94,0.35)',
            background: 'transparent',
            color: golden ? '#f8e8c8' : 'rgba(248,232,200,0.4)',
            cursor: golden ? 'pointer' : 'not-allowed',
            fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11,
          }}
        >Export bundle ✦</button>
        <button
          type="button"
          onClick={wipe}
          disabled={!golden}
          data-testid={`golden-audio-wipe-${profileName}`}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: '1px solid rgba(224,57,42,0.35)',
            background: 'transparent',
            color: golden ? '#e0392a' : 'rgba(224,57,42,0.4)',
            cursor: golden ? 'pointer' : 'not-allowed',
            fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11,
          }}
        >Wipe</button>
        <span style={{
          marginLeft: 'auto', color: 'rgba(248,232,200,0.55)', fontSize: 11,
          fontFamily: 'ui-monospace, Menlo, monospace',
        }} data-testid={`golden-audio-meta-${profileName}`}>
          device {deviceFingerprint()} · pipeline {PIPELINE_VERSION}
        </span>
      </div>
      {drift && (
        <div style={{ marginTop: 10, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }} data-testid={`golden-audio-drift-${profileName}`}>
          <Row k="Δ rawRoomToneRMS" v={drift.deltas.rawRoomToneRMS} />
          <Row k="Δ presenceFloor" v={drift.deltas.presenceFloorSample} />
          <Row k="Δ peakMax" v={drift.deltas.peakLevelMaxDelta} />
          <Row k="Δ presenceMoments" v={drift.deltas.voicePresenceMomentsCount} />
          <Row k="pipelineVersionDrift" v={drift.pipelineVersionDrift ? 'YES' : 'no'} />
          <Row k="profileHashDrift" v={drift.profileHashDrift ? 'YES' : 'no'} />
          <Row k="recordingMimeMatch" v={drift.recordingMimeMatch ? 'yes' : 'NO'} />
        </div>
      )}
    </div>
  );
};

const Row = ({ k, v }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between',
    padding: '4px 0', borderBottom: '1px solid rgba(212,175,94,0.10)',
    fontVariantNumeric: 'tabular-nums',
  }}>
    <span style={{ color: 'rgba(248,232,200,0.55)' }}>{k}</span>
    <span style={{ color: '#f8e8c8' }} data-testid={`debug-row-${k}`}>{String(v)}</span>
  </div>
);

/**
 * ParitySnapshot ✦ iter203 — export parity diagnostic.
 *
 * Captures TWO frames at the same instant:
 *
 *   PREVIEW — what the guest sees on the live preview surface
 *             (drawn with the pipeline's mirrorPreview semantics)
 *   EXPORT  — what the export pipeline would actually write
 *             (cam.captureStill — mirrorExport semantics)
 *
 * Then renders both side-by-side AND a third canvas using
 * mix-blend-mode: difference so any drift between what the guest
 * SEES and what is PRESERVED jumps out as bright pixels.
 *
 * Expected behaviour per profile:
 *
 *   booth / portrait / strip / guestbook (mirror-preview-only)
 *     → PREVIEW and EXPORT are horizontal mirrors of each other.
 *       The difference panel will show a near-full bright field —
 *       this is the CORRECT, intentional asymmetry.
 *
 *   lens (mirror-export)
 *     → PREVIEW and EXPORT are byte-identical (both mirrored).
 *       The difference panel must be near-black. Any bright pixels
 *       indicate an "almost correct" drift bug — exactly the class
 *       of failure the user flagged.
 *
 *   spin360 (no-mirror)
 *     → PREVIEW and EXPORT are byte-identical (neither mirrored).
 *       Difference panel must be near-black.
 */
const ParitySnapshot = ({ cam }) => {
  const [snap, setSnap] = useState(null);

  const capture = async () => {
    const v = cam.videoRef.current;
    if (!v || !v.videoWidth) return;

    // PREVIEW frame — apply mirrorPreview the same way the live <video>
    // CSS transform does.
    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = v.videoWidth;
    previewCanvas.height = v.videoHeight;
    const pctx = previewCanvas.getContext('2d');
    if (cam.mirrorPreview) {
      pctx.translate(previewCanvas.width, 0);
      pctx.scale(-1, 1);
    }
    pctx.drawImage(v, 0, 0);

    // EXPORT frame — what the production pipeline actually writes.
    const exportDataUrl = await cam.captureStill();
    setSnap({
      previewDataUrl: previewCanvas.toDataURL('image/jpeg', 0.9),
      exportDataUrl,
      mirrorPreview: cam.mirrorPreview,
      mirrorExport: cam.mirrorExport,
    });
  };

  const TilePair = snap && (
    <div data-testid="parity-snapshot-output" style={{
      marginTop: 12, display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 10,
    }}>
      {[
        { label: 'PREVIEW', src: snap.previewDataUrl, testid: 'parity-preview' },
        { label: 'EXPORT', src: snap.exportDataUrl, testid: 'parity-export' },
      ].map(({ label, src, testid }) => (
        <div key={label}>
          <div style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: 11, color: 'rgba(248,232,200,0.6)',
            marginBottom: 4, letterSpacing: '0.06em',
          }}>{label}</div>
          <img
            src={src} alt={label}
            data-testid={testid}
            style={{ width: '100%', borderRadius: 6, background: '#000' }}
          />
        </div>
      ))}
      {/* DIFFERENCE — mix-blend-mode applied between two stacked
          images. Any visible bright pixels indicate drift. */}
      <div>
        <div style={{
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          fontSize: 11, color: 'rgba(248,232,200,0.6)',
          marginBottom: 4, letterSpacing: '0.06em',
        }}>DIFFERENCE</div>
        <div
          data-testid="parity-difference"
          style={{
            position: 'relative', width: '100%',
            borderRadius: 6, overflow: 'hidden',
            background: '#000',
            aspectRatio: String(cam.profile.aspectRatio),
          }}
        >
          <img src={snap.previewDataUrl} alt="" style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'cover',
          }} />
          <img src={snap.exportDataUrl} alt="" style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'cover',
            mixBlendMode: 'difference',
          }} />
        </div>
        <div style={{
          fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: 10, color: 'rgba(248,232,200,0.55)',
          marginTop: 4,
        }}>
          expect {snap.mirrorPreview === snap.mirrorExport ? 'near-black' : 'horizontal-mirror flash'}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 18 }}>
      <button
        type="button"
        onClick={capture}
        data-testid="parity-snapshot-capture"
        style={{
          padding: '8px 16px', borderRadius: 999,
          border: '1px solid rgba(212,175,94,0.35)',
          background: 'rgba(212,175,94,0.10)',
          color: '#f8e8c8', cursor: 'pointer',
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          letterSpacing: '0.04em', fontSize: 12,
        }}
        disabled={cam.status !== 'live'}
      >
        Capture parity snapshot ✦
      </button>
      {TilePair}
      <GoldenCompositionControls cam={cam} snap={snap} />
    </div>
  );
};

/**
 * GoldenCompositionControls ✦ iter207 — capture / compare / export
 * a golden composition reference for the current profile + device.
 *
 * Storage: localStorage, keyed by (profile × deviceFingerprint).
 * Bundle includes pipelineVersion + profileHash so iter208 can
 * still distinguish "same profile values, different pipeline
 * behaviour" — Craig's iter207 directive.
 */
const GoldenCompositionControls = ({ cam, snap }) => {
  const [golden, setGolden] = useState(() => loadGolden('composition', cam.profile && Object.keys(BOOTH_CAMERA_PROFILES).find((k) => BOOTH_CAMERA_PROFILES[k] === cam.profile)));
  const profileName = Object.keys(BOOTH_CAMERA_PROFILES).find((k) => BOOTH_CAMERA_PROFILES[k] === cam.profile);

  const save = () => {
    if (!snap || !profileName) return;
    const { bundle } = saveGolden({
      kind: 'composition',
      profileName,
      profileConfigSnapshot: cam.profile,
      payload: {
        previewDataUrl: snap.previewDataUrl,
        exportDataUrl: snap.exportDataUrl,
        mirrorPreview: snap.mirrorPreview,
        mirrorExport: snap.mirrorExport,
        videoSignature: `${cam.videoRef.current?.videoWidth || 0}×${cam.videoRef.current?.videoHeight || 0}`,
        dimensionsStable: cam.dimensionsStable,
      },
    });
    setGolden(bundle);
  };

  const wipe = () => {
    if (!profileName) return;
    saveGolden({ kind: 'composition', profileName, profileConfigSnapshot: cam.profile, payload: null });
    setGolden(null);
  };

  return (
    <div style={{ marginTop: 14 }} data-testid={`golden-composition-${profileName}`}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={save}
          disabled={!snap}
          data-testid={`golden-composition-save-${profileName}`}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: '1px solid rgba(212,175,94,0.35)',
            background: 'rgba(212,175,94,0.10)',
            color: '#f8e8c8', cursor: snap ? 'pointer' : 'not-allowed',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: 11, opacity: snap ? 1 : 0.4,
          }}
        >Capture golden ✦</button>
        <button
          type="button"
          onClick={() => golden && downloadGolden(golden)}
          disabled={!golden}
          data-testid={`golden-composition-export-${profileName}`}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: '1px solid rgba(212,175,94,0.35)',
            background: 'transparent',
            color: golden ? '#f8e8c8' : 'rgba(248,232,200,0.4)',
            cursor: golden ? 'pointer' : 'not-allowed',
            fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11,
          }}
        >Export bundle ✦</button>
        <button
          type="button"
          onClick={wipe}
          disabled={!golden}
          data-testid={`golden-composition-wipe-${profileName}`}
          style={{
            padding: '6px 14px', borderRadius: 999,
            border: '1px solid rgba(224,57,42,0.35)',
            background: 'transparent',
            color: golden ? '#e0392a' : 'rgba(224,57,42,0.4)',
            cursor: golden ? 'pointer' : 'not-allowed',
            fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11,
          }}
        >Wipe</button>
        <span style={{
          marginLeft: 'auto', color: 'rgba(248,232,200,0.55)', fontSize: 11,
          fontFamily: 'ui-monospace, Menlo, monospace',
        }} data-testid={`golden-composition-meta-${profileName}`}>
          device {deviceFingerprint()} · pipeline {PIPELINE_VERSION}
        </span>
      </div>
      {golden?.payload?.previewDataUrl && snap && (
        <div style={{
          marginTop: 10, display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
        }} data-testid={`golden-composition-compare-${profileName}`}>
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11, color: 'rgba(248,232,200,0.6)', marginBottom: 4, letterSpacing: '0.06em' }}>GOLDEN PREVIEW</div>
            <img src={golden.payload.previewDataUrl} alt="" style={{ width: '100%', borderRadius: 6, background: '#000' }} />
          </div>
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11, color: 'rgba(248,232,200,0.6)', marginBottom: 4, letterSpacing: '0.06em' }}>CURRENT PREVIEW</div>
            <img src={snap.previewDataUrl} alt="" style={{ width: '100%', borderRadius: 6, background: '#000' }} />
          </div>
          <div>
            <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11, color: 'rgba(248,232,200,0.6)', marginBottom: 4, letterSpacing: '0.06em' }}>DIFFERENCE</div>
            <div style={{
              position: 'relative', width: '100%',
              borderRadius: 6, overflow: 'hidden',
              background: '#000',
              aspectRatio: String(cam.profile.aspectRatio),
            }} data-testid={`golden-composition-diff-${profileName}`}>
              <img src={golden.payload.previewDataUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              <img src={snap.previewDataUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', mixBlendMode: 'difference' }} />
            </div>
            <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10, color: 'rgba(248,232,200,0.55)', marginTop: 4 }}>
              {golden.profileHash === hashProfile(cam.profile)
                ? 'profileHash match — expect near-black'
                : 'profileHash drift — config changed since golden capture'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfilePanel = ({ profileName }) => {
  const cam = useBoothCameraPipeline({ profile: profileName });
  const frameRef = useRef(null);
  const [tick, setTick] = useState(0);

  // Re-render the telemetry block every 250ms so dimensionsStable
  // and videoWidth × videoHeight update visibly.
  React.useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    cam.start();
    return () => cam.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileName]);

  const v = cam.videoRef.current;
  const vw = v?.videoWidth || 0;
  const vh = v?.videoHeight || 0;
  const lensReport = profileName === 'lens'
    ? assertCanvasInvariants(null)  // canvas is owned by LensNotePage; this is a no-op for the debug route
    : null;

  return (
    <div data-testid={`composition-panel-${profileName}`} style={{
      display: 'grid', gridTemplateColumns: 'minmax(280px, 460px) 1fr',
      gap: 28, padding: 18,
      border: '1px solid rgba(212,175,94,0.18)',
      borderRadius: 14, background: 'rgba(0,0,0,0.4)',
    }}>
      <div ref={frameRef} style={{
        position: 'relative', aspectRatio: String(cam.profile.aspectRatio),
        background: '#000', borderRadius: 10, overflow: 'hidden',
      }}>
        <video
          ref={cam.videoRef} playsInline muted
          style={cam.previewStyle}
          data-testid={`composition-video-${profileName}`}
        />
        <cam.FramingGuide longPressTarget={frameRef} visibleOverride />
      </div>
      <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
        <h3 style={{
          margin: 0, padding: '0 0 8px',
          color: '#c19a4b', fontFamily: 'Georgia, serif',
          fontStyle: 'italic', fontWeight: 500, fontSize: 18,
        }}>
          {profileName} ✦
        </h3>
        <Row k="aspectRatio" v={cam.profile.aspectRatio.toFixed(4)} />
        <Row k="resolution" v={`${cam.profile.resolution.width}×${cam.profile.resolution.height}`} />
        <Row k="facingMode" v={cam.facingMode} />
        <Row k="frameRate" v={`${cam.profile.frameRate.ideal} / ${cam.profile.frameRate.max}`} />
        <Row k="audio" v={cam.profile.audio} />
        <Row k="mirrorMode" v={cam.profile.mirrorMode} />
        <Row k="mirrorPreview" v={cam.mirrorPreview} />
        <Row k="mirrorExport" v={cam.mirrorExport} />
        <Row k="compositionFrame" v={cam.profile.compositionFrame} />
        <Row k="headroomGuide" v={cam.profile.headroomGuide} />
        <Row k="subjectAnchor" v={cam.profile.subjectAnchor} />
        <Row k="captureDistance" v={cam.profile.captureDistance} />
        <Row k="safeZone" v={JSON.stringify(cam.profile.safeZone)} />
        <Row k="—" v={`runtime tick ${tick}`} />
        <Row k="status" v={cam.status} />
        <Row k="dimensionsStable" v={cam.dimensionsStable} />
        <Row k="videoSignature" v={`${vw}×${vh}`} />
        <Row k="recordingMime" v={cam.recordingMime || '—'} />
        <Row k="devicePixelRatio" v={typeof window !== 'undefined' ? window.devicePixelRatio : '—'} />
        {lensReport && <Row k="lensCanvasInvariants" v={JSON.stringify(lensReport)} />}
        {cam.error && (
          <div style={{ marginTop: 12, color: '#e0392a' }} data-testid={`composition-error-${profileName}`}>
            {cam.error}
          </div>
        )}
        {/* iter203 — export parity snapshot. Captures preview vs.
            export at the same instant and renders a mix-blend-mode
            difference. Bright pixels = "almost correct" drift bug. */}
        <ParitySnapshot cam={cam} />
      </div>
    </div>
  );
};

const CompositionLanguagePage = () => {
  const [selected, setSelected] = useState('booth');
  // iter205 — Audio diagnostic. Lives in the same composition-language
  // page (philosophical unity) — voice and visuals are facets of the
  // same authored participation system.
  const [selectedAudio, setSelectedAudio] = useState('voice_message');

  return (
    <div
      data-testid="composition-language-debug-page"
      style={{
        minHeight: '100vh', background: '#08070a',
        color: '#f8e8c8',
        padding: '40px 28px 80px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <header style={{ maxWidth: 1100, margin: '0 auto 24px' }}>
        <p style={{
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          color: 'rgba(248,232,200,0.55)', margin: 0,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          fontSize: 12,
        }}>
          EventDrop ✦ Internal
        </p>
        <h1 style={{
          fontFamily: 'Georgia, "Cormorant Garamond", serif',
          fontWeight: 500, fontSize: 36, margin: '4px 0 6px',
          color: '#f8e8c8',
        }}>
          Composition language
        </h1>
        <p style={{ margin: 0, color: 'rgba(248,232,200,0.55)', fontSize: 14 }}>
          Per-profile preview + framing geometry + runtime stabilisation
          telemetry. One camera at a time so we never request more
          tracks than the host hardware can satisfy.
          MIRROR vocabulary: PREVIEW_ONLY={MIRROR.PREVIEW_ONLY}, EXPORT={MIRROR.EXPORT}, NONE={MIRROR.NONE}.
        </p>
        <div style={{ marginTop: 18, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {PROFILE_NAMES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setSelected(p)}
              data-testid={`composition-pick-${p}`}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                border: '1px solid rgba(212,175,94,0.35)',
                background: selected === p ? 'rgba(212,175,94,0.18)' : 'transparent',
                color: selected === p ? '#f8e8c8' : 'rgba(248,232,200,0.65)',
                fontFamily: 'Georgia, serif', fontStyle: 'italic',
                cursor: 'pointer',
              }}
            >{p}</button>
          ))}
        </div>
      </header>
      <main style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Re-mount the panel when the user selects a new profile —
            forces the pipeline to release the previous stream and
            request the new constraints. */}
        <ProfilePanel key={selected} profileName={selected} />

        {/* iter205 — voice-side diagnostic. Same page (one authored
            participation system); separate pipeline (audio is its
            own emotional infrastructure). */}
        <div style={{ marginTop: 36 }}>
          <h2 style={{
            fontFamily: 'Georgia, "Cormorant Garamond", serif',
            fontWeight: 500, fontSize: 24, margin: '0 0 10px',
            color: '#f8e8c8',
          }}>
            Audio ✦
          </h2>
          <p style={{ margin: 0, color: 'rgba(248,232,200,0.55)', fontSize: 13 }}>
            Participation audio pipeline — intelligibility-first, room
            tone preserved. Tail-only silence trim. Live RMS / presence
            floor / clipping telemetry.
          </p>
          <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {AUDIO_PROFILE_NAMES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setSelectedAudio(p)}
                data-testid={`audio-pick-${p}`}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: '1px solid rgba(212,175,94,0.35)',
                  background: selectedAudio === p ? 'rgba(212,175,94,0.18)' : 'transparent',
                  color: selectedAudio === p ? '#f8e8c8' : 'rgba(248,232,200,0.65)',
                  fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  cursor: 'pointer',
                }}
              >{p}</button>
            ))}
          </div>
          <AudioPanel key={selectedAudio} profileName={selectedAudio} />
        </div>
      </main>
    </div>
  );
};

export default CompositionLanguagePage;
